import type { Api } from "../api";
import { empty, h, label, pill } from "../dom";
import { when } from "../format";
import { inquiryOrder, inquiryState } from "../inquiries";
import { navigate } from "../nav";
import { checkField } from "./shared";
import type { Inquiry, Page } from "../types";

// Inquiries (inquiries, orders.read): the website's contact form, newest first, those needing someone first: HELD
// (past the day's limit of emails to sales@) and NOTICE_FAILED weren't emailed, so someone reads them here and replies
// from sales@ in Gmail. Bots are set aside and shown only when asked. Read only: nothing is sent to the inquirer.

const DAYS = [30, 90, 365];

export async function inquiries(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const days = DAYS.includes(Number(query.get("days"))) ? Number(query.get("days")) : 30;
  const spam = query.get("spam") === "1";
  const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  const page = await api.tool<Page<Inquiry>>("inquiries", { since, limit: 200, ...(spam ? { include_spam: true } : {}) });
  const rows = inquiryOrder(page.rows);
  const toReply = rows.filter((r) => inquiryState(r.status).needsReply).length;

  const daysSelect = h("select", { id: "days" }, DAYS.map((d) => h("option", { value: String(d), selected: d === days }, `${d} days`)));
  const { field: spamField, box: spamBox } = checkField("spam", "Bots too", spam);
  const go = () => navigate(`/inquiries?${new URLSearchParams({ days: daysSelect.value, ...(spamBox.checked ? { spam: "1" } : {}) })}`);
  daysSelect.addEventListener("change", go);
  spamBox.addEventListener("change", go);

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Website inquiries"),
      h("p", { class: "lede" }, rows.length
        ? `${rows.length}${page.next_cursor ? "+" : ""} in the last ${days} days${toReply ? `; ${toReply} ${toReply === 1 ? "needs" : "need"} someone to reply` : ""}.`
        : `None in the last ${days} days.`),
      h("p", { class: "caption" }, "Each one is emailed to sales@tare.group; reply from there. Ones held past the day's limit, " +
        "or not emailed, are marked here.")),
    h("form", { class: "filters" },
      h("div", { class: "field" }, h("label", { for: "days" }, "Received in"), daysSelect),
      spamField),
    rows.length
      ? h("ul", { class: "cards" }, rows.map((r) => {
          const st = inquiryState(r.status);
          return h("li", { class: "card" },
            h("div", { class: "row-act" }, h("span", { class: "strong" }, r.name), r.company ? h("span", null, `· ${r.company}`) : null,
              pill(st.text.toLowerCase(), st.tone)),
            h("span", { class: "caption" }, [when(r.receivedAt), r.topic].filter(Boolean).join(" · ")),
            h("span", { class: "num" }, r.email, r.phone ? ` · ${r.phone}` : ""),
            h("p", { class: "prose" }, r.message),
            r.noticeError ? h("span", { class: "caption" }, `Why it wasn't emailed: ${r.noticeError}`) : null,
            r.spamReason ? h("span", { class: "caption" }, `Set aside because: ${r.spamReason}`) : null);
        }))
      : empty("Nothing here", spam ? "No inquiries, bots included." : "No inquiries yet. Bots are left out unless you ask for them."),
    page.next_cursor ? h("p", { class: "caption" }, "More than 200: choose a shorter period.") : null,
    label("From the contact form on tare.group"));
}
